// Privacy-safe, provider-neutral product analytics.
//
// No SDK, no cookies, no IP/user-agent capture. Events are best-effort POSTs to
// the first-party backend and are mirrored into window.__tmAnalytics for tests.

import { GAME_VERSION } from '../version';

export type AnalyticsEventName =
  | 'first_open'
  | 'tutorial_started'
  | 'tutorial_step_completed'
  | 'first_move'
  | 'first_chomp'
  | 'first_correct'
  | 'first_wrong'
  | 'scan_used'
  | 'route_bonus_triggered'
  | 'board_started'
  | 'board_completed'
  | 'board_abandoned'
  | 'run_completed'
  | 'result_explanation_opened'
  | 'next_board_tapped'
  | 'topic_selected'
  | 'custom_topic_requested'
  | 'custom_topic_succeeded'
  | 'share_opened'
  | 'share_completed'
  | 'challenge_opened'
  | 'challenge_completed'
  | 'return_day';

export type BoardSource = 'seed' | 'generated' | 'fallback' | 'unknown';

export interface AnalyticsDims {
  experimentVariant?: string;
  boardSource?: BoardSource;
  topicCategory?: string;
  difficulty?: number;
  boardIndex?: number;
  elapsedMs?: number;
  result?: string;
  failureReason?: string;
}

export interface AnalyticsEvent extends AnalyticsDims {
  event: AnalyticsEventName;
  anonymousPlayerId: string;
  sessionId: string;
  gameVersion: string;
}

interface AnalyticsDebugBridge {
  events: AnalyticsEvent[];
  track(event: AnalyticsEventName, dims?: AnalyticsDims): void;
  flush(): Promise<void>;
}

type AnalyticsWindow = Window & { __tmAnalytics?: AnalyticsDebugBridge };

const INSTALL_ID_KEY = 'trivia.analytics.anonymous_id.v1';
const SESSION_ID_KEY = 'trivia.analytics.session_id.v1';
const ONCE_PREFIX = 'trivia.analytics.once.';
const LAST_OPEN_DAY_KEY = 'trivia.analytics.last_open_day.v1';
const RECENT_EVENT_LIMIT = 100;

const recentEvents: AnalyticsEvent[] = [];

function apiBase(): string {
  const raw = import.meta.env?.VITE_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

function storage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function randomId(prefix: string): string {
  const cryptoApi = window.crypto;
  if (cryptoApi?.randomUUID) return `${prefix}_${cryptoApi.randomUUID()}`;
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    const body = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${body}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2)}`;
}

function stableId(key: string, prefix: string, kind: 'local' | 'session'): string {
  const store = storage(kind);
  const existing = store?.getItem(key);
  if (existing && /^[a-zA-Z0-9_.:-]+$/.test(existing)) return existing;
  const next = randomId(prefix);
  try {
    store?.setItem(key, next);
  } catch {
    // Storage can be blocked in private modes; keep the event best-effort.
  }
  return next;
}

function cleanText(raw: unknown, max = 80): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const text = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)
    .trim();
  return text && /^[a-zA-Z0-9_:. -]+$/.test(text) ? text : undefined;
}

function cleanNumber(raw: unknown): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : undefined;
}

function normalizeDims(dims: AnalyticsDims = {}): AnalyticsDims {
  return {
    experimentVariant: cleanText(dims.experimentVariant),
    boardSource: dims.boardSource,
    topicCategory: cleanText(dims.topicCategory),
    difficulty: cleanNumber(dims.difficulty),
    boardIndex: cleanNumber(dims.boardIndex),
    elapsedMs: cleanNumber(dims.elapsedMs),
    result: cleanText(dims.result),
    failureReason: cleanText(dims.failureReason),
  };
}

function buildEvent(event: AnalyticsEventName, dims?: AnalyticsDims): AnalyticsEvent {
  const out = normalizeDims(dims);
  return {
    event,
    anonymousPlayerId: stableId(INSTALL_ID_KEY, 'anon', 'local'),
    sessionId: stableId(SESSION_ID_KEY, 'sess', 'session'),
    gameVersion: GAME_VERSION,
    ...out,
    // Default the cohort so EVERY event carries the A/B variant with zero call-site edits. An explicit
    // dim (none today) still wins; otherwise the install-stable assignment below tags the event.
    experimentVariant: out.experimentVariant ?? getExperimentVariant(),
  };
}

function remember(event: AnalyticsEvent): void {
  recentEvents.push(event);
  if (recentEvents.length > RECENT_EVENT_LIMIT) recentEvents.splice(0, recentEvents.length - RECENT_EVENT_LIMIT);
}

async function send(event: AnalyticsEvent): Promise<void> {
  const base = apiBase();
  if (!base) return;
  try {
    await fetch(`${base}/events`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true,
    });
  } catch {
    // Analytics never blocks play.
  }
}

export function track(event: AnalyticsEventName, dims?: AnalyticsDims): void {
  // P0.6 contract: analytics is best-effort and must NEVER throw into the game loop. buildEvent ->
  // stableId reads localStorage, and some WebViews throw on getItem itself (not just on access), so
  // an unguarded track() called from GameScene.create()/dispatch() could abort board init. Swallow.
  try {
    const built = buildEvent(event, dims);
    remember(built);
    void send(built);
  } catch {
    // Never let a storage/serialization fault break gameplay.
  }
}

export function trackOnce(key: string, event: AnalyticsEventName, dims?: AnalyticsDims): void {
  // Once-per-INSTALL dedup (localStorage), not once-per-session: the first_* activation family
  // (first_open/first_move/first_chomp/first_correct/first_wrong) must fire exactly once per install
  // or the activation funnel is inflated by every returning tab/session. localStorage is install-
  // scoped and matches anonymousPlayerId; sessionStorage was the bug (it re-fired every new session).
  const scopedKey = `${ONCE_PREFIX}${key}`;
  const store = storage('local');
  try {
    if (store?.getItem(scopedKey) === '1') return;
    store?.setItem(scopedKey, '1');
  } catch {
    // Without storage, fall through and emit; still no PII.
  }
  track(event, dims);
}

function utcDay(): string {
  return new Date().toISOString().slice(0, 10);
}

export function trackFirstOpen(): void {
  // Fully guarded: runs unconditionally on every boot (main.ts) and reads localStorage, which can
  // throw in some WebViews — must never break startup. (P0.6: analytics never crashes gameplay.)
  try {
    trackOnce('first_open', 'first_open');
    const today = utcDay();
    const store = storage('local');
    const last = store?.getItem(LAST_OPEN_DAY_KEY) ?? '';
    if (last && last !== today) track('return_day', { result: dayDelta(last, today) === 1 ? 'next_day' : 'later' });
    try {
      store?.setItem(LAST_OPEN_DAY_KEY, today);
    } catch {
      // Best-effort only.
    }
  } catch {
    // Storage access/operation faulted — skip first-open/return-day rather than abort boot.
  }
}

function dayDelta(a: string, b: string): number {
  const aMs = Date.parse(`${a}T00:00:00Z`);
  const bMs = Date.parse(`${b}T00:00:00Z`);
  if (!Number.isFinite(aMs) || !Number.isFinite(bMs)) return 0;
  return Math.round((bMs - aMs) / 86_400_000);
}

// --- Experiment cohorting ---------------------------------------------------------------------
// One install-stable flag drives BOTH the menu layout (daily-home vs classic-home) and the
// experimentVariant analytics dim, so the Daily reposition is honestly A/B-measurable. Deterministic:
// hashes the existing anonymousPlayerId — no Math.random, no new identifier shipped. A ?home=daily|classic
// query override forces a cohort for QA. To ship 100% daily, make getExperimentVariant() return a constant.
let assignedVariant: string | undefined;

function variantOverride(): string | undefined {
  try {
    const v = new URLSearchParams(window.location.search).get('home');
    if (v === 'daily') return 'daily_home';
    if (v === 'classic') return 'classic_home';
  } catch {
    // No URL available (SSR/headless edge) — fall through to the hash assignment.
  }
  return undefined;
}

export function getExperimentVariant(): string {
  if (assignedVariant) return assignedVariant;
  // Ship 100% of the Daily reposition ("Daily as the default identity"): EVERY install gets the treatment
  // so the imminent cold-player Feel playtest sees the INTENDED design, not a coin flip that shows half of
  // testers the old PLAY hero (which would muddy the fun signal). A ?home=classic|daily override stays for
  // QA. Cannot throw (no storage access on the default path), preserving P0.6 "analytics never breaks play".
  //
  // To re-enable a MEASURED 50/50 holdback for the later growth phase, swap the line below for an install-
  // hash split: const h = (s)=>{let x=0x811c9dc5;for(const c of s)x=Math.imul(x^c.charCodeAt(0),0x01000193);return x>>>0;};
  //   assignedVariant = (h(stableId(INSTALL_ID_KEY,'anon','local')) & 1) === 0 ? 'daily_home' : 'classic_home';
  assignedVariant = variantOverride() ?? 'daily_home';
  return assignedVariant;
}

export function initExperiments(): void {
  // Lock the cohort once at boot so the menu layout and every analytics event agree on the same value.
  try {
    getExperimentVariant();
  } catch {
    // Storage faults must never break boot; the variant lazily resolves on first use instead.
  }
}

export function installAnalyticsDebugBridge(): void {
  const win = window as AnalyticsWindow;
  if (win.__tmAnalytics) return;
  win.__tmAnalytics = {
    events: recentEvents,
    track,
    async flush() {
      const pending = [...recentEvents];
      await Promise.all(pending.map((event) => send(event)));
    },
  };
}
